import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Download, Plus, Trash2, ArrowLeft, FileText, Search, Loader2,
  PackageCheck, X, Users, Tag, AlertCircle,
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDocumentExport() {
  const navigate = useNavigate();

  // The download queue: a Map keyed by document_id -> {personnel_id, personnel_name, document_id, document_name, document_type, expiration_date}
  const [queue, setQueue] = useState({});
  const [downloading, setDownloading] = useState(false);

  // Mode toggle
  const [mode, setMode] = useState('byType'); // 'byType' | 'byPerson'

  // Shared
  const [credentialTypes, setCredentialTypes] = useState([]);
  const [personnel, setPersonnel] = useState([]);

  // By-type mode state
  const [selectedType, setSelectedType] = useState('');
  const [typeRows, setTypeRows] = useState([]); // [{personnel_id, name, document_id, document_name, expiration_date, has_file}]
  const [typeChecked, setTypeChecked] = useState(new Set());
  const [typeSearch, setTypeSearch] = useState('');
  const [loadingType, setLoadingType] = useState(false);

  // By-person mode state
  const [selectedPerson, setSelectedPerson] = useState('');
  const [personDocs, setPersonDocs] = useState([]);
  const [personChecked, setPersonChecked] = useState(new Set());
  const [personSearch, setPersonSearch] = useState('');
  const [loadingPerson, setLoadingPerson] = useState(false);

  // Initial load
  useEffect(() => {
    (async () => {
      try {
        const [typesRes, personnelRes] = await Promise.all([
          axios.get(`${API}/credentials/types`),
          axios.get(`${API}/personnel?status=Active`),
        ]);
        setCredentialTypes(typesRes.data?.types || []);
        setPersonnel(personnelRes.data || []);
      } catch (e) {
        toast.error('Failed to load setup data');
      }
    })();
  }, []);

  // Load by-type rows when a type is picked
  useEffect(() => {
    if (mode !== 'byType' || !selectedType) {
      setTypeRows([]);
      setTypeChecked(new Set());
      return;
    }
    (async () => {
      setLoadingType(true);
      try {
        const res = await axios.get(`${API}/admin/credentials/by-type/${encodeURIComponent(selectedType)}`);
        setTypeRows(res.data || []);
        setTypeChecked(new Set());
      } catch (e) {
        toast.error('Failed to load credentials for that type');
      } finally {
        setLoadingType(false);
      }
    })();
  }, [mode, selectedType]);

  // Load person's docs when person picked
  useEffect(() => {
    if (mode !== 'byPerson' || !selectedPerson) {
      setPersonDocs([]);
      setPersonChecked(new Set());
      return;
    }
    (async () => {
      setLoadingPerson(true);
      try {
        const res = await axios.get(`${API}/personnel/${selectedPerson}/credentials`);
        // Backend returns array of credential records — keep only non-archived ones with files
        const items = (res.data || []).filter(d => !d.archived);
        setPersonDocs(items);
        setPersonChecked(new Set());
      } catch (e) {
        toast.error('Failed to load that person\'s documents');
      } finally {
        setLoadingPerson(false);
      }
    })();
  }, [mode, selectedPerson]);

  const filteredTypeRows = useMemo(() => {
    if (!typeSearch.trim()) return typeRows;
    const s = typeSearch.toLowerCase();
    return typeRows.filter(r => r.name.toLowerCase().includes(s));
  }, [typeRows, typeSearch]);

  const filteredPersonnel = useMemo(() => {
    if (!personSearch.trim()) return personnel;
    const s = personSearch.toLowerCase();
    return personnel.filter(p => `${p.first_name} ${p.last_name}`.toLowerCase().includes(s));
  }, [personnel, personSearch]);

  const personById = useMemo(() => {
    const map = {};
    personnel.forEach(p => { map[p.id] = `${p.first_name || ''} ${p.last_name || ''}`.trim(); });
    return map;
  }, [personnel]);

  // ----- Queue ops -----
  const addRowsToQueue = (rows) => {
    if (!rows.length) {
      toast.info('Nothing selected');
      return;
    }
    setQueue(prev => {
      const next = { ...prev };
      let added = 0;
      rows.forEach(r => {
        if (!next[r.document_id]) {
          next[r.document_id] = r;
          added += 1;
        }
      });
      toast.success(`Added ${added} document(s) to queue${rows.length - added > 0 ? ` (${rows.length - added} already in queue)` : ''}`);
      return next;
    });
  };

  const handleAddByType = () => {
    const rows = filteredTypeRows
      .filter(r => typeChecked.has(r.document_id) && r.has_file)
      .map(r => ({
        document_id: r.document_id,
        personnel_id: r.personnel_id,
        personnel_name: r.name,
        document_name: r.document_name,
        document_type: r.document_type,
        expiration_date: r.expiration_date,
      }));
    addRowsToQueue(rows);
    setTypeChecked(new Set());
  };

  const handleAddByPerson = () => {
    const personName = personById[selectedPerson] || 'Unknown';
    const rows = personDocs
      .filter(d => personChecked.has(d.id))
      .map(d => ({
        document_id: d.id,
        personnel_id: selectedPerson,
        personnel_name: personName,
        document_name: d.document_name || d.document_type,
        document_type: d.document_type,
        expiration_date: d.expiration_date,
      }));
    addRowsToQueue(rows);
    setPersonChecked(new Set());
  };

  const removeFromQueue = (docId) => {
    setQueue(prev => {
      const next = { ...prev };
      delete next[docId];
      return next;
    });
  };

  const clearQueue = () => {
    if (!Object.keys(queue).length) return;
    if (!window.confirm('Clear the entire queue?')) return;
    setQueue({});
  };

  const handleDownload = async () => {
    const ids = Object.keys(queue);
    if (!ids.length) {
      toast.error('Queue is empty');
      return;
    }
    setDownloading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/admin/credentials/download-queue-zip`,
        { document_ids: ids },
        { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' }
      );
      const blob = new Blob([response.data], { type: 'application/zip' });
      const cd = response.headers['content-disposition'] || '';
      const match = cd.match(/filename="?([^";]+)"?/);
      const filename = match ? match[1] : `document_export_${Date.now()}.zip`;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);

      const included = response.headers['x-documents-included'];
      const skipped = response.headers['x-documents-skipped'];
      if (skipped && parseInt(skipped) > 0) {
        toast.warning(`Downloaded ${included} document(s); skipped ${skipped} (file missing on server)`);
      } else {
        toast.success(`Downloaded ${included || ids.length} document(s)`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Download failed');
    } finally {
      setDownloading(false);
    }
  };

  // Group queue by personnel for display
  const queueByPerson = useMemo(() => {
    const groups = {};
    Object.values(queue).forEach(item => {
      const key = item.personnel_id;
      if (!groups[key]) groups[key] = { name: item.personnel_name, items: [] };
      groups[key].items.push(item);
    });
    return Object.values(groups).sort((a, b) => a.name.localeCompare(b.name));
  }, [queue]);

  const queueCount = Object.keys(queue).length;

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <Button variant="ghost" size="sm" onClick={() => navigate('/admin')} data-testid="back-to-admin-btn">
        <ArrowLeft size={16} className="mr-1" /> Back to Admin
      </Button>

      <div>
        <h1 className="font-heading text-3xl font-bold text-foreground flex items-center gap-2">
          <PackageCheck className="text-primary" /> Document Export
        </h1>
        <p className="text-muted-foreground mt-1 max-w-3xl">
          Build a download queue across multiple credential types and people, then export everything as one zip
          (organized into per-person folders). Switch modes anytime — items keep stacking up in the queue until you download.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* LEFT: Add to queue */}
        <Card className="p-5 space-y-4" data-testid="add-to-queue-card">
          <div className="flex items-center gap-1 bg-muted rounded-lg p-1 w-fit">
            <button
              onClick={() => setMode('byType')}
              className={`px-3 py-1.5 rounded-md text-sm flex items-center gap-1.5 transition-colors ${
                mode === 'byType' ? 'bg-background shadow-sm font-medium' : 'text-muted-foreground hover:text-foreground'
              }`}
              data-testid="mode-by-type"
            >
              <Tag size={14} /> By Credential Type
            </button>
            <button
              onClick={() => setMode('byPerson')}
              className={`px-3 py-1.5 rounded-md text-sm flex items-center gap-1.5 transition-colors ${
                mode === 'byPerson' ? 'bg-background shadow-sm font-medium' : 'text-muted-foreground hover:text-foreground'
              }`}
              data-testid="mode-by-person"
            >
              <Users size={14} /> By Personnel
            </button>
          </div>

          {mode === 'byType' ? (
            <div className="space-y-3">
              <div>
                <Label>Credential type</Label>
                <select
                  value={selectedType}
                  onChange={(e) => setSelectedType(e.target.value)}
                  className="flex h-10 w-full rounded-md border bg-background px-3 py-2 text-sm"
                  data-testid="cred-type-select"
                >
                  <option value="">— Select a type —</option>
                  {credentialTypes.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>

              {selectedType && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <p className="text-xs text-muted-foreground">
                      {loadingType ? 'Loading…' : `${filteredTypeRows.length} personnel with ${selectedType}`}
                    </p>
                    <div className="relative">
                      <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
                      <Input
                        placeholder="Filter by name…"
                        value={typeSearch}
                        onChange={(e) => setTypeSearch(e.target.value)}
                        className="pl-7 h-7 text-xs w-48"
                        data-testid="type-search"
                      />
                    </div>
                  </div>

                  <div className="max-h-80 overflow-y-auto border rounded-md">
                    {loadingType ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="animate-spin text-muted-foreground" size={20} />
                      </div>
                    ) : filteredTypeRows.length === 0 ? (
                      <p className="text-center text-muted-foreground text-sm py-6">No personnel with this credential.</p>
                    ) : (
                      <table className="w-full text-sm">
                        <thead className="sticky top-0 bg-muted/60 backdrop-blur">
                          <tr className="text-left text-xs uppercase text-muted-foreground">
                            <th className="p-2 w-8">
                              <input
                                type="checkbox"
                                checked={filteredTypeRows.length > 0 && filteredTypeRows.every(r => typeChecked.has(r.document_id))}
                                onChange={(e) => {
                                  if (e.target.checked) setTypeChecked(new Set(filteredTypeRows.map(r => r.document_id)));
                                  else setTypeChecked(new Set());
                                }}
                                data-testid="type-select-all"
                              />
                            </th>
                            <th className="p-2">Name</th>
                            <th className="p-2">Document</th>
                            <th className="p-2">Expiration</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredTypeRows.map(r => (
                            <tr
                              key={r.document_id}
                              className={`border-t hover:bg-muted/30 ${!r.has_file ? 'opacity-50' : ''}`}
                              data-testid={`type-row-${r.document_id}`}
                            >
                              <td className="p-2">
                                <input
                                  type="checkbox"
                                  disabled={!r.has_file}
                                  checked={typeChecked.has(r.document_id)}
                                  onChange={() => {
                                    setTypeChecked(prev => {
                                      const n = new Set(prev);
                                      if (n.has(r.document_id)) n.delete(r.document_id); else n.add(r.document_id);
                                      return n;
                                    });
                                  }}
                                />
                              </td>
                              <td className="p-2 font-medium">{r.name}</td>
                              <td className="p-2 text-muted-foreground">
                                {r.document_name}
                                {!r.has_file && <span className="text-[10px] text-amber-600 ml-1">(no file)</span>}
                                {queue[r.document_id] && <span className="text-[10px] text-emerald-600 ml-1">(in queue)</span>}
                              </td>
                              <td className="p-2 text-muted-foreground">{r.expiration_date || '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>

                  <Button onClick={handleAddByType} disabled={typeChecked.size === 0} className="w-full" data-testid="add-by-type-btn">
                    <Plus size={14} className="mr-1" /> Add {typeChecked.size || 0} to Queue
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              <div>
                <Label>Personnel</Label>
                <div className="relative">
                  <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    placeholder="Search personnel…"
                    value={personSearch}
                    onChange={(e) => setPersonSearch(e.target.value)}
                    className="pl-7 mb-2"
                    data-testid="person-search"
                  />
                </div>
                <select
                  value={selectedPerson}
                  onChange={(e) => setSelectedPerson(e.target.value)}
                  className="flex h-10 w-full rounded-md border bg-background px-3 py-2 text-sm"
                  data-testid="person-select"
                >
                  <option value="">— Select a person —</option>
                  {filteredPersonnel.map(p => (
                    <option key={p.id} value={p.id}>{p.first_name} {p.last_name} {p.discipline ? `(${p.discipline})` : ''}</option>
                  ))}
                </select>
              </div>

              {selectedPerson && (
                <div className="space-y-2">
                  <p className="text-xs text-muted-foreground">
                    {loadingPerson ? 'Loading…' : `${personDocs.length} current document(s)`}
                  </p>
                  <div className="max-h-80 overflow-y-auto border rounded-md">
                    {loadingPerson ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="animate-spin text-muted-foreground" size={20} />
                      </div>
                    ) : personDocs.length === 0 ? (
                      <p className="text-center text-muted-foreground text-sm py-6">No current documents on file.</p>
                    ) : (
                      <table className="w-full text-sm">
                        <thead className="sticky top-0 bg-muted/60 backdrop-blur">
                          <tr className="text-left text-xs uppercase text-muted-foreground">
                            <th className="p-2 w-8">
                              <input
                                type="checkbox"
                                checked={personDocs.length > 0 && personDocs.every(d => personChecked.has(d.id))}
                                onChange={(e) => {
                                  if (e.target.checked) setPersonChecked(new Set(personDocs.map(d => d.id)));
                                  else setPersonChecked(new Set());
                                }}
                                data-testid="person-select-all"
                              />
                            </th>
                            <th className="p-2">Document</th>
                            <th className="p-2">Type</th>
                            <th className="p-2">Expiration</th>
                          </tr>
                        </thead>
                        <tbody>
                          {personDocs.map(d => (
                            <tr key={d.id} className="border-t hover:bg-muted/30" data-testid={`person-doc-row-${d.id}`}>
                              <td className="p-2">
                                <input
                                  type="checkbox"
                                  checked={personChecked.has(d.id)}
                                  onChange={() => {
                                    setPersonChecked(prev => {
                                      const n = new Set(prev);
                                      if (n.has(d.id)) n.delete(d.id); else n.add(d.id);
                                      return n;
                                    });
                                  }}
                                />
                              </td>
                              <td className="p-2 font-medium">
                                {d.document_name || d.document_type}
                                {queue[d.id] && <span className="text-[10px] text-emerald-600 ml-1">(in queue)</span>}
                              </td>
                              <td className="p-2 text-muted-foreground">{d.document_type}</td>
                              <td className="p-2 text-muted-foreground">{d.expiration_date || '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>

                  <Button onClick={handleAddByPerson} disabled={personChecked.size === 0} className="w-full" data-testid="add-by-person-btn">
                    <Plus size={14} className="mr-1" /> Add {personChecked.size || 0} to Queue
                  </Button>
                </div>
              )}
            </div>
          )}
        </Card>

        {/* RIGHT: Download queue */}
        <Card className="p-5 space-y-3" data-testid="queue-card">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold flex items-center gap-2">
              <FileText size={16} /> Download Queue
              <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-md">
                {queueCount} item{queueCount !== 1 ? 's' : ''} · {queueByPerson.length} {queueByPerson.length === 1 ? 'person' : 'people'}
              </span>
            </h3>
            {queueCount > 0 && (
              <Button variant="ghost" size="sm" onClick={clearQueue} data-testid="clear-queue-btn">
                Clear
              </Button>
            )}
          </div>

          {queueCount === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <FileText className="mx-auto text-muted-foreground/50" size={32} />
              <p className="mt-2 text-sm">Queue is empty.</p>
              <p className="text-xs mt-1 max-w-sm mx-auto">
                Use the panel on the left to add documents by credential type or by individual personnel.
                Items stack up here until you download them all as one zip.
              </p>
            </div>
          ) : (
            <>
              <div className="max-h-[480px] overflow-y-auto space-y-3 pr-1">
                {queueByPerson.map(group => (
                  <div key={group.name} className="rounded-md border">
                    <div className="px-3 py-2 bg-muted/40 text-sm font-medium border-b flex items-center justify-between">
                      <span>{group.name}</span>
                      <span className="text-xs text-muted-foreground">{group.items.length} item(s)</span>
                    </div>
                    <ul className="divide-y">
                      {group.items.map(item => (
                        <li key={item.document_id} className="flex items-center justify-between px-3 py-2 text-sm">
                          <div className="min-w-0 flex-1">
                            <div className="font-medium truncate">{item.document_name}</div>
                            <div className="text-xs text-muted-foreground">
                              {item.document_type}
                              {item.expiration_date && <> · exp {item.expiration_date}</>}
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeFromQueue(item.document_id)}
                            data-testid={`remove-queue-${item.document_id}`}
                            className="ml-2"
                          >
                            <X size={14} />
                          </Button>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>

              <Button
                onClick={handleDownload}
                disabled={downloading}
                className="w-full"
                data-testid="download-queue-btn"
              >
                {downloading ? (
                  <><Loader2 size={14} className="animate-spin mr-1" /> Building zip…</>
                ) : (
                  <><Download size={14} className="mr-1" /> Download {queueCount} Document{queueCount !== 1 ? 's' : ''} as Zip</>
                )}
              </Button>
            </>
          )}
        </Card>
      </div>

      <Card className="p-4 bg-blue-50 border-blue-200">
        <div className="flex items-start gap-3">
          <AlertCircle size={18} className="text-blue-600 mt-0.5 shrink-0" />
          <div className="text-sm text-blue-900">
            <p className="font-semibold">How it works</p>
            <ul className="list-disc list-inside mt-1 space-y-0.5 text-blue-800 text-xs">
              <li>Pick "By Credential Type", choose a type, check the people you want, click <b>Add to Queue</b>.</li>
              <li>Switch to "By Personnel", pick a therapist, check any of their docs, click <b>Add to Queue</b>.</li>
              <li>Repeat as many times as you like — items keep stacking up.</li>
              <li>When ready, click <b>Download</b> to grab one zip with each person's files in their own folder.</li>
            </ul>
          </div>
        </div>
      </Card>
    </div>
  );
}
